import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  HiOutlineCheck,
  HiOutlineClipboardDocumentList,
  HiOutlineDocumentText,
  HiOutlineLightBulb,
  HiOutlinePrinter,
  HiOutlineShieldCheck,
  HiOutlineViewfinderCircle,
  HiOutlineWrenchScrewdriver,
  HiOutlineXMark,
} from "react-icons/hi2";
import type { IconType } from "react-icons";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { uploadCase } from "../../store/user/userSlice";
import { useToast } from "../utilities/Toast";
import type { CreatedPortalCase } from "./caseDetailsFormNew";

const EMERGENCE_OPTIONS = [
  "URIS recommended on case",
  "Tissue level",
  "Bone level",
  "Custom",
];

const QUAD = {
  ul: [18, 17, 16, 15, 14, 13, 12, 11],
  ur: [21, 22, 23, 24, 25, 26, 27, 28],
  ll: [48, 47, 46, 45, 44, 43, 42, 41],
  lr: [31, 32, 33, 34, 35, 36, 37, 38],
} as const;

export type UrisOrderEntitlements = {
  surgicalTimingGuide: boolean;
  addImplants: boolean;
  customAbutment: boolean;
  printedModel: boolean;
  packageId?: number | null;
  packageName?: string | null;
};

export const DEFAULT_URIS_ENTITLEMENTS: UrisOrderEntitlements = {
  surgicalTimingGuide: true,
  addImplants: true,
  customAbutment: true,
  printedModel: true,
  packageId: null,
  packageName: null,
};

type OptionKey =
  | "surgicalTimingGuide"
  | "addImplants"
  | "customAbutment"
  | "printedModel";

type Arch = "upper" | "lower" | "both";
type YesNo = "" | "yes" | "no";
type ChartKey = "implant" | "restoration" | "extraction";

const OPTIONS: {
  key: OptionKey;
  title: string;
  body: string;
  Icon: IconType;
  emergence?: boolean;
}[] = [
  {
    key: "surgicalTimingGuide",
    title: "Surgical Timing Guide",
    body: "Includes design and printing of the surgical guide with guide sleeves.",
    Icon: HiOutlineViewfinderCircle,
  },
  {
    key: "addImplants",
    title: "Add Implants",
    body: "Include URIS fixtures for this patient.",
    Icon: HiOutlineLightBulb,
  },
  {
    key: "customAbutment",
    title: "Custom Abutment",
    body: "Custom titanium abutment designed for the implant site.",
    Icon: HiOutlineWrenchScrewdriver,
    emergence: true,
  },
  {
    key: "printedModel",
    title: "Printed Model",
    body: "Includes a printed 3D model with soft tissue and a digital lab analog.",
    Icon: HiOutlinePrinter,
  },
];

const CHARTS: {
  key: ChartKey;
  label: string;
  required: boolean;
  hint: string;
}[] = [
  {
    key: "implant",
    label: "Implant positions",
    required: true,
    hint: "Select each planned implant site.",
  },
  {
    key: "restoration",
    label: "Restoration positions",
    required: false,
    hint: "Teeth to be restored on the implants.",
  },
  {
    key: "extraction",
    label: "Extraction sites",
    required: false,
    hint: "Teeth to be extracted before or at surgery.",
  },
];

const emptyCharts = (): Record<ChartKey, number[]> => ({
  implant: [],
  restoration: [],
  extraction: [],
});

const emptySelected = (
  entitlements: UrisOrderEntitlements,
): Record<OptionKey, boolean> => ({
  surgicalTimingGuide: Boolean(entitlements.surgicalTimingGuide),
  addImplants: false,
  customAbutment: false,
  printedModel: false,
});

const UrisSurgicalGuideOrder = ({
  onCaseCreated,
  entitlements = DEFAULT_URIS_ENTITLEMENTS,
}: {
  onCaseCreated: (created: CreatedPortalCase) => void;
  entitlements?: UrisOrderEntitlements;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAppSelector((state) => state.auth);
  const [referenceName, setReferenceName] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selected, setSelected] = useState<Record<OptionKey, boolean>>(() =>
    emptySelected(entitlements),
  );
  const [abutmentProfile, setAbutmentProfile] = useState(EMERGENCE_OPTIONS[0]);
  const [arch, setArch] = useState<Arch>("upper");
  const [charts, setCharts] = useState(emptyCharts);
  const [sinusLift, setSinusLift] = useState<YesNo>("");
  const [boneGrafting, setBoneGrafting] = useState<YesNo>("");
  const [notes, setNotes] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const showUpper = arch === "upper" || arch === "both";
  const showLower = arch === "lower" || arch === "both";

  useEffect(() => {
    setSelected((current) => {
      const next = { ...current };
      for (const option of OPTIONS) {
        if (!entitlements[option.key]) next[option.key] = false;
      }
      if (
        entitlements.surgicalTimingGuide &&
        !OPTIONS.some((option) => next[option.key] && entitlements[option.key])
      ) {
        next.surgicalTimingGuide = true;
      }
      return next;
    });
  }, [entitlements]);

  const toggleOption = (key: OptionKey) => {
    if (!entitlements[key]) return;
    setSelected((current) => ({ ...current, [key]: !current[key] }));
  };

  const toggleTooth = (chart: ChartKey, tooth: number) => {
    setCharts((current) => {
      const list = current[chart];
      const next = list.includes(tooth)
        ? list.filter((n) => n !== tooth)
        : [...list, tooth].sort((a, b) => a - b);
      return { ...current, [chart]: next };
    });
  };

  const validationError = useMemo(() => {
    if (!referenceName.trim()) return "Enter a reference name.";
    if (!OPTIONS.some((option) => selected[option.key] && entitlements[option.key])) {
      return "Choose at least one order option available on your package.";
    }
    if (!charts.implant.length) return "Select at least one implant position.";
    if (!sinusLift) return "Choose Yes or No for sinus lift / augmentation.";
    if (!boneGrafting) return "Choose Yes or No for bone grafting.";
    if (!agreed) return "Agree to the terms & conditions to continue.";
    return "";
  }, [
    referenceName,
    selected,
    entitlements,
    charts.implant.length,
    sinusLift,
    boneGrafting,
    agreed,
  ]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (validationError || !user?.user_id) {
      setShowErrors(true);
      showToast(validationError || "Could not create the case", "error");
      return;
    }

    const chosen = OPTIONS.filter(
      (option) => selected[option.key] && entitlements[option.key],
    );
    const services: Record<string, Record<string, string>> = {
      urisOrder: {
        surgicalTimingGuide: selected.surgicalTimingGuide ? "Yes" : "No",
        addImplants: selected.addImplants ? "Yes" : "No",
        customAbutment: selected.customAbutment ? "Yes" : "No",
        customAbutmentEmergence: selected.customAbutment ? abutmentProfile : "",
        printedModel: selected.printedModel ? "Yes" : "No",
      },
    };
    if (selected.surgicalTimingGuide) {
      services.surgicalGuide = { guideType: "Surgical Timing Guide" };
    }
    if (selected.printedModel) {
      services["digital-model"] = {
        modelType: "Printed model with soft tissue",
      };
    }

    setSubmitting(true);
    const result = await dispatch(
      uploadCase({
        user_id: user.user_id,
        case_owner: referenceName.trim(),
        service_type: chosen.map((option) => option.title).join(", "),
        details_json: JSON.stringify({
          patientName: referenceName.trim(),
          caseDueDate: dueDate,
          treatmentPlan: {
            arch,
            implantPositions: charts.implant,
            restorationPositions: charts.restoration,
            extractionSites: charts.extraction,
            sinusLift,
            boneGrafting,
            notes: notes.trim(),
          },
          services,
        }),
        createdBy: user.user_name || "Client",
      } as never),
    );
    setSubmitting(false);

    if (uploadCase.fulfilled.match(result)) {
      const created = result.payload as CreatedPortalCase;
      showToast("Case created", "success");
      onCaseCreated({
        case_id: created.case_id,
        case_owner: created.case_owner || referenceName.trim(),
        box_folder_id: created.box_folder_id ?? null,
      });
      return;
    }
    showToast(String(result.payload || "Could not create the case"), "error");
  };

  const fieldRow =
    "grid gap-4 rounded-[10px] border border-[#303136] bg-[#24252a] p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:items-start md:gap-7";
  const inputClass =
    "h-[42px] w-full rounded-lg border border-[#37383e] bg-[#141517] px-3 text-sm text-[#f2f0f1] outline-none placeholder:text-[#77757a] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)]";
  const tileBase =
    "relative flex min-h-[72px] flex-col items-center justify-center gap-2 rounded-[10px] border px-3 py-3 text-[13px] font-semibold transition";
  const tileOn =
    "border-[#BF1E2E] bg-[rgba(191,30,46,0.15)] text-white";
  const tileOff =
    "border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white";

  return (
    <form
      onSubmit={submit}
      className="uris-scope mx-auto flex max-w-[1376px] flex-col gap-5 text-[#eceaeb]"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-[#BF1E2E] pb-3">
        <div>
          <p className="text-[11.5px] text-[#9C9A9B]">New case</p>
          <h1 className="font-[Montserrat,sans-serif] text-[17px] font-bold text-white">
            Surgical Guide Order
          </h1>
        </div>
        <p className="text-[12.5px] text-[#9C9A9B]">
          Fields marked <span className="ml-0.5 font-bold text-[#E5A443]">*</span>{" "}
          are required
        </p>
      </div>

      {entitlements.packageName ? (
        <p className="rounded-[10px] border border-[#303136] bg-[#24252a] px-4 py-2.5 text-[13px] text-[#bdbbbc]">
          Package: <span className="font-semibold text-white">{entitlements.packageName}</span>
          . Greyed options are not included.
        </p>
      ) : null}

      <section className="flex flex-col gap-3 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 pb-5 pt-[18px]">
        <h2 className="mb-1 flex items-center gap-2.5 border-b border-[#2e2f34] pb-3.5 font-[Montserrat,sans-serif] text-[15px] font-bold text-white">
          <HiOutlineClipboardDocumentList className="h-[18px] w-[18px] shrink-0 text-[#F06A74]" />
          Case Information
        </h2>
        <div className={fieldRow}>
          <label className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]" htmlFor="uris-ref">
            Reference Name<span className="ml-0.5 font-bold text-[#E5A443]">*</span>
          </label>
          <input
            id="uris-ref"
            value={referenceName}
            onChange={(e) => setReferenceName(e.target.value)}
            placeholder="Patient name or job number"
            className={`${inputClass} ${
              showErrors && !referenceName.trim() ? "border-[#BF1E2E]" : ""
            }`}
          />
        </div>
        <div className={fieldRow}>
          <label className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]" htmlFor="uris-due">
            Case Due Date
          </label>
          <input
            id="uris-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={`${inputClass} max-w-[260px] [color-scheme:dark]`}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 pb-5 pt-[18px]">
        <h2 className="mb-1 flex items-center gap-2.5 border-b border-[#2e2f34] pb-3.5 font-[Montserrat,sans-serif] text-[15px] font-bold text-white">
          <HiOutlineViewfinderCircle className="h-[18px] w-[18px] shrink-0 text-[#F06A74]" />
          Order Options
          <span className="ml-1.5 font-[DM_Sans,sans-serif] text-[11.5px] font-normal text-[#9C9A9B]">
            select all that apply
          </span>
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(230px,1fr))]">
          {OPTIONS.map((option) => {
            const allowed = entitlements[option.key];
            const active = selected[option.key] && allowed;
            return (
              <div
                key={option.key}
                className={`flex flex-col gap-3 rounded-xl border p-3 ${
                  !allowed
                    ? "border-[#2a2b30] bg-[#1a1b1f] opacity-55"
                    : active
                      ? "border-[#BF1E2E] bg-[#26222a]"
                      : "border-[#303136] bg-[#24252a]"
                }`}
              >
                <button
                  type="button"
                  aria-pressed={active}
                  aria-disabled={!allowed}
                  disabled={!allowed}
                  title={
                    allowed
                      ? undefined
                      : "Not included in your purchased package"
                  }
                  onClick={() => toggleOption(option.key)}
                  className={`relative flex min-h-[170px] w-full flex-col items-start gap-2.5 rounded-[10px] border p-3.5 text-left ${
                    !allowed
                      ? "cursor-not-allowed border-[#2e2f34] bg-[#141517]"
                      : active
                        ? "border-[rgba(191,30,46,0.6)] bg-[rgba(191,30,46,0.12)]"
                        : "border-[#37383e] bg-[#141517] hover:border-[#5a5b61]"
                  }`}
                >
                  <span
                    className={`absolute right-3.5 top-3.5 flex h-5 w-5 items-center justify-center rounded-[5px] border-[1.5px] ${
                      active
                        ? "border-[#BF1E2E] bg-[#BF1E2E] text-white"
                        : "border-[#55565c] text-transparent"
                    }`}
                  >
                    <HiOutlineCheck className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <span
                    className={`flex h-11 w-11 items-center justify-center rounded-[10px] ${
                      active
                        ? "bg-[rgba(191,30,46,0.22)] text-[#F06A74]"
                        : "bg-[#24252a] text-[#bdbbbc]"
                    }`}
                  >
                    <option.Icon className="h-6 w-6" />
                  </span>
                  <span className="pr-6 font-[Montserrat,sans-serif] text-sm font-bold leading-snug text-white">
                    {option.title}
                  </span>
                  <span className="text-[12.5px] leading-relaxed text-[#a9a7a8]">
                    {option.body}
                  </span>
                  {!allowed ? (
                    <span className="mt-auto text-[11px] font-semibold uppercase tracking-wide text-[#8a888b]">
                      Not in package
                    </span>
                  ) : null}
                </button>
                {option.emergence ? (
                  <label className="block text-xs font-semibold text-[#bdbbbc]">
                    Abutment emergence profile
                    <span className="relative mt-1.5 block">
                      <select
                        value={abutmentProfile}
                        disabled={!active}
                        onChange={(e) => setAbutmentProfile(e.target.value)}
                        className={`${inputClass} appearance-none pr-10 disabled:cursor-not-allowed disabled:opacity-45`}
                      >
                        {EMERGENCE_OPTIONS.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </span>
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 pb-5 pt-[18px]">
        <h2 className="mb-1 flex items-center gap-2.5 border-b border-[#2e2f34] pb-3.5 font-[Montserrat,sans-serif] text-[15px] font-bold text-white">
          <HiOutlineDocumentText className="h-[18px] w-[18px] shrink-0 text-[#F06A74]" />
          Treatment Plan
        </h2>
        <div className={fieldRow}>
          <div className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]">
            Arch to treatment plan
            <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
          </div>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3" role="group">
            {(
              [
                ["upper", "Maxilla / Upper"],
                ["lower", "Mandible / Lower"],
                ["both", "Both"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={arch === value}
                onClick={() => setArch(value)}
                className={`${tileBase} ${arch === value ? tileOn : tileOff}`}
              >
                {arch === value ? (
                  <HiOutlineCheck className="absolute right-2 top-2 h-3.5 w-3.5 text-[#F06A74]" />
                ) : null}
                {label}
              </button>
            ))}
          </div>
        </div>

        {CHARTS.map((chart) => {
          const selectedTeeth = charts[chart.key];
          return (
            <div key={chart.key} className={fieldRow}>
              <div className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]">
                {chart.label}
                {chart.required ? (
                  <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
                ) : (
                  <span className="ml-1.5 text-[11.5px] font-normal text-[#9C9A9B]">
                    optional
                  </span>
                )}
                <span className="mt-1.5 block text-xs font-normal leading-snug text-[#9C9A9B]">
                  {chart.hint}
                </span>
                {selectedTeeth.length > 0 ? (
                  <span className="mt-2 inline-block text-[11.5px] font-semibold text-[#F3A0A6]">
                    {selectedTeeth.length}{" "}
                    {selectedTeeth.length === 1 ? "tooth" : "teeth"} selected
                  </span>
                ) : null}
              </div>
              <div className="rounded-[10px] border border-[#37383e] bg-[#141517] px-3 py-1">
                <div className="flex justify-between py-1.5 pl-[62px] text-[10.5px] tracking-wide text-[#77757a]">
                  <span>Patient&apos;s right</span>
                  <span>Patient&apos;s left</span>
                </div>
                {showUpper ? (
                  <div className="grid grid-cols-[52px_minmax(0,1fr)_1px_minmax(0,1fr)] items-center gap-2.5 border-t border-[#2a2b30] py-1.5 first:border-t-0">
                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#8a888b]">
                      Upper
                    </span>
                    <div className="grid grid-cols-8 gap-1">
                      {QUAD.ul.map((tooth) => (
                        <ToothButton
                          key={tooth}
                          tooth={tooth}
                          on={selectedTeeth.includes(tooth)}
                          onToggle={() => toggleTooth(chart.key, tooth)}
                        />
                      ))}
                    </div>
                    <div className="self-stretch bg-[#37383e]" />
                    <div className="grid grid-cols-8 gap-1">
                      {QUAD.ur.map((tooth) => (
                        <ToothButton
                          key={tooth}
                          tooth={tooth}
                          on={selectedTeeth.includes(tooth)}
                          onToggle={() => toggleTooth(chart.key, tooth)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                {showLower ? (
                  <div className="grid grid-cols-[52px_minmax(0,1fr)_1px_minmax(0,1fr)] items-center gap-2.5 border-t border-[#2a2b30] py-1.5">
                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#8a888b]">
                      Lower
                    </span>
                    <div className="grid grid-cols-8 gap-1">
                      {QUAD.ll.map((tooth) => (
                        <ToothButton
                          key={tooth}
                          tooth={tooth}
                          on={selectedTeeth.includes(tooth)}
                          onToggle={() => toggleTooth(chart.key, tooth)}
                        />
                      ))}
                    </div>
                    <div className="self-stretch bg-[#37383e]" />
                    <div className="grid grid-cols-8 gap-1">
                      {QUAD.lr.map((tooth) => (
                        <ToothButton
                          key={tooth}
                          tooth={tooth}
                          on={selectedTeeth.includes(tooth)}
                          onToggle={() => toggleTooth(chart.key, tooth)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </section>

      <section className="flex flex-col gap-3 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 pb-5 pt-[18px]">
        <h2 className="mb-1 flex items-center gap-2.5 border-b border-[#2e2f34] pb-3.5 font-[Montserrat,sans-serif] text-[15px] font-bold text-white">
          <HiOutlineClipboardDocumentList className="h-[18px] w-[18px] shrink-0 text-[#F06A74]" />
          Treatment Planning Details
        </h2>
        <div className={fieldRow}>
          <div className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]">
            Sinus lift / augmentation
            <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
          </div>
          <YesNoTiles value={sinusLift} onChange={setSinusLift} />
        </div>
        <div className={fieldRow}>
          <div className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]">
            Bone grafting
            <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
          </div>
          <YesNoTiles value={boneGrafting} onChange={setBoneGrafting} />
        </div>
        <div className={fieldRow}>
          <label className="pt-2.5 text-[13.5px] font-semibold text-[#d6d4d5]" htmlFor="uris-notes">
            Notes
            <span className="ml-1.5 text-[11.5px] font-normal text-[#9C9A9B]">
              optional
            </span>
          </label>
          <div>
            <textarea
              id="uris-notes"
              value={notes}
              maxLength={2000}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Treatment plan preferences, patient medical condition, hygiene, bite, preferred stock healing abutment sizes, etc."
              className="h-[116px] w-full resize-y rounded-lg border border-[#37383e] bg-[#141517] px-3 py-2.5 text-sm leading-relaxed text-[#f2f0f1] outline-none placeholder:text-[#77757a] focus:border-[#BF1E2E] focus:shadow-[0_0_0_3px_rgba(191,30,46,0.28)]"
            />
            <span className="mt-1.5 block text-right text-xs text-[#9C9A9B]">
              {notes.length.toLocaleString()} / 2,000 characters
            </span>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] px-5 pb-5 pt-[18px]">
        <h2 className="mb-1 flex items-center gap-2.5 border-b border-[#2e2f34] pb-3.5 font-[Montserrat,sans-serif] text-[15px] font-bold text-white">
          <HiOutlineShieldCheck className="h-[18px] w-[18px] shrink-0 text-[#F06A74]" />
          Disclaimer
        </h2>
        <div className="rounded-[10px] border border-[#303136] bg-[#24252a] p-4">
          <p className="m-0 text-[13.5px] leading-relaxed text-[#bdbbbc]">
            I certify I have read and fully understand the terms and words within
            this document and the explanations referred to or implied, and that
            after thorough deliberation, I give my consent for the production of
            the surgical guide related to the placement of dental implant(s) as
            presented to me during the consultation and treatment plan
            presentation by URIS Inc. and their staff or as described in this
            document.
          </p>
          <label className="mt-3 flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-[#e9e7e8]">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="h-[18px] w-[18px] cursor-pointer accent-[#BF1E2E]"
            />
            <span>
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F3A0A6] hover:text-white"
              >
                terms &amp; conditions
              </a>
              <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
            </span>
          </label>
          {showErrors && !agreed ? (
            <p className="mt-2 text-xs text-[#F06A74]">Required before saving.</p>
          ) : null}
        </div>
      </section>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={() => navigate("/dashboard/my-cases")}
          className="h-[46px] rounded-[9px] border border-[#3d3e44] bg-transparent px-8 text-sm font-bold text-[#d6d4d5] hover:border-[#6a6b71] hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="h-[46px] rounded-[9px] border border-[#BF1E2E] bg-[#BF1E2E] px-8 text-sm font-bold text-white hover:bg-[#a51927] disabled:opacity-60"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
};

function ToothButton({
  tooth,
  on,
  onToggle,
}: {
  tooth: number;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      className={`h-9 rounded-[7px] border text-[13px] font-medium ${
        on
          ? "border-[#BF1E2E] bg-[#BF1E2E] font-bold text-white"
          : "border-transparent bg-transparent text-[#b3b1b2] hover:bg-[#26272c] hover:text-white"
      }`}
    >
      {tooth}
    </button>
  );
}

function YesNoTiles({
  value,
  onChange,
}: {
  value: YesNo;
  onChange: (next: YesNo) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2.5" role="group">
      {(
        [
          ["yes", "Yes", HiOutlineCheck],
          ["no", "No", HiOutlineXMark],
        ] as const
      ).map(([val, label, Icon]) => {
        const active = value === val;
        return (
          <button
            key={val}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(val)}
            className={`relative flex min-h-[72px] flex-col items-center justify-center gap-2 rounded-[10px] border px-3 py-3 text-[13px] font-semibold ${
              active
                ? "border-[#BF1E2E] bg-[rgba(191,30,46,0.15)] text-white"
                : "border-[#37383e] bg-[#141517] text-[#bdbbbc] hover:border-[#5a5b61] hover:text-white"
            }`}
          >
            <Icon className="h-5 w-5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default UrisSurgicalGuideOrder;
